import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Combobox,
  ConfirmDialog,
  DataTable,
  Drawer,
  Input,
  KpiTile,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { recipesApi } from '../recipes/recipesApi';
import { catalogApi } from '../catalog/catalogApi';
import { usageApi } from './usageApi';
import './usage.css';

let tmpId = 0;
const tmpKey = () => `uv-${++tmpId}`;
const num = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? null : n; };
const qty = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 }));

const varianceTone = (n) => {
  if (n == null) return 'neutral';
  if (n > 0.0001) return 'danger';
  if (n < -0.0001) return 'success';
  return 'neutral';
};

export default function UsageAnalysisFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { activePropertyId } = useProperty();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');

  const [menuItems, setMenuItems] = useState([]);
  const [locations, setLocations] = useState([]);

  const [name, setName] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [scopeLocationId, setScopeLocationId] = useState('');
  const [threshold, setThreshold] = useState('5');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([]);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const previewSeq = useRef(0);

  const loadLookups = useCallback(async () => {
    const [recs, locs] = await Promise.all([
      recipesApi.list({ type: 'MenuItem' }).catch(() => null),
      catalogApi.listLocations(activePropertyId).catch(() => null),
    ]);
    setMenuItems(recs || []);
    setLocations(locs || []);
  }, [activePropertyId]);

  const loadAnalysis = useCallback(async () => {
    if (!editing) { setLoading(false); return; }
    setLoading(true);
    try {
      const a = await usageApi.get(id);
      setName(a.name || '');
      setPeriodStart(a.periodStart ? a.periodStart.slice(0, 10) : '');
      setPeriodEnd(a.periodEnd ? a.periodEnd.slice(0, 10) : '');
      setScopeLocationId(a.scopeLocationId || '');
      setThreshold(String(a.varianceThresholdPercent ?? 5));
      setNotes(a.notes || '');
      setLines((a.salesLines || []).map((l) => ({ key: tmpKey(), recipeId: l.recipeId, name: l.name, unitsSold: String(l.unitsSold ?? '') })));
      setPreview(a);
    } catch (err) {
      toast.error(t('Could not load analysis'), { description: err?.message });
      navigate('/usage-variance');
    } finally {
      setLoading(false);
    }
  }, [editing, id, navigate, toast, t]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { loadAnalysis(); }, [loadAnalysis]);

  const usedIds = useMemo(() => new Set(lines.map((l) => l.recipeId)), [lines]);
  const itemOptions = useMemo(
    () => menuItems
      .filter((m) => !usedIds.has(m.id))
      .map((m) => ({ value: m.id, label: m.name, description: m.number })),
    [menuItems, usedIds],
  );
  const locationOptions = useMemo(
    () => [{ value: '', label: t('All locations') }, ...locations.map((l) => ({ value: l.id, label: l.name }))],
    [locations, t],
  );

  const addItem = (recipeId) => {
    const m = menuItems.find((x) => x.id === recipeId);
    if (!m) return;
    setLines((prev) => [...prev, { key: tmpKey(), recipeId, name: m.name, unitsSold: '' }]);
  };
  const updateUnits = (key, units) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, unitsSold: units } : l)));
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const runImport = () => {
    const byNumber = new Map(menuItems.filter((m) => m.number).map((m) => [m.number.toLowerCase(), m]));
    const byName = new Map(menuItems.map((m) => [m.name.trim().toLowerCase(), m]));
    const next = new Map(lines.map((l) => [l.recipeId, { ...l }]));
    let matched = 0;
    const unmatched = [];
    importText.split(/\r?\n/).forEach((raw) => {
      const row = raw.trim();
      if (!row) return;
      const parts = row.split(/[\t,;]/).map((p) => p.trim());
      if (parts.length < 2) { unmatched.push(row); return; }
      const units = num(parts[parts.length - 1]);
      const keyText = parts.slice(0, parts.length - 1).join(' ').trim().toLowerCase();
      const m = byNumber.get(keyText) || byName.get(keyText);
      if (!m || units == null) { unmatched.push(row); return; }
      next.set(m.id, { key: next.get(m.id)?.key || tmpKey(), recipeId: m.id, name: m.name, unitsSold: String(units) });
      matched += 1;
    });
    setLines(Array.from(next.values()));
    setImportOpen(false);
    setImportText('');
    if (matched) {
      const base = matched === 1 ? t('Imported {count} line', { count: matched }) : t('Imported {count} lines', { count: matched });
      toast.success(unmatched.length ? `${base} · ${t('{count} unmatched', { count: unmatched.length })}` : base);
    } else {
      toast.error(t('No rows matched a menu item by name or reference.'));
    }
  };

  const datesValid = Boolean(periodStart && periodEnd);

  const previewSignature = useMemo(
    () => JSON.stringify([periodStart, periodEnd, scopeLocationId, num(threshold) ?? 5, lines.map((l) => [l.recipeId, num(l.unitsSold) ?? 0])]),
    [periodStart, periodEnd, scopeLocationId, threshold, lines],
  );
  useEffect(() => {
    if (!datesValid) return undefined;
    const seq = ++previewSeq.current;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const res = await usageApi.preview({
          periodStart: new Date(periodStart).toISOString(),
          periodEnd: new Date(periodEnd).toISOString(),
          scopeLocationId: scopeLocationId || null,
          varianceThresholdPercent: num(threshold) ?? 5,
          lines: lines.filter((l) => l.recipeId).map((l) => ({ recipeId: l.recipeId, unitsSold: num(l.unitsSold) ?? 0 })),
        });
        if (seq === previewSeq.current) setPreview(res);
      } catch {
        /* keep last preview */
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSignature]);

  const currency = preview?.summary?.currency || 'USD';
  const fmtMoney = (n) => `${currency} ${formatAmount(n)}`;
  const summary = preview?.summary;
  const varianceLines = preview?.variance || [];

  const buildPayload = () => ({
    name: name.trim(),
    periodStart: new Date(periodStart).toISOString(),
    periodEnd: new Date(periodEnd).toISOString(),
    scopeLocationId: scopeLocationId || null,
    varianceThresholdPercent: num(threshold) ?? 5,
    notes: notes.trim() || null,
    lines: lines.filter((l) => l.recipeId).map((l) => ({ recipeId: l.recipeId, unitsSold: num(l.unitsSold) ?? 0 })),
  });

  const save = async () => {
    if (!name.trim()) { toast.error(t('Enter a name for the analysis.')); return; }
    if (!datesValid) { toast.error(t('Set the period start and end dates.')); return; }
    if (lines.filter((l) => l.recipeId).length === 0) { toast.error(t('Add at least one menu item sold.')); return; }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        const updated = await usageApi.update(id, payload);
        setPreview(updated);
        toast.success(t('Analysis saved'));
      } else {
        const created = await usageApi.create(payload);
        toast.success(t('{number} created', { number: created.number }));
        navigate(`/usage-variance/${created.id}`);
      }
    } catch (err) {
      toast.error(t('Could not save analysis'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    setSubmitting(true);
    try {
      await usageApi.remove(id);
      toast.success(t('Analysis deleted'));
      navigate('/usage-variance');
    } catch (err) {
      toast.error(t('Could not delete analysis'), { description: err?.message });
      setDeleteOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'item',
      header: t('Item'),
      render: (l) => (
        <div className="uv-item">
          <span className="uv-item__name">{l.itemName}</span>
          {l.flagged && <Badge tone="warning">{t('flag')}</Badge>}
          {!l.costKnown && <Badge tone="neutral">{t('no cost')}</Badge>}
          {l.note && <span className="uv-item__note">{l.note}</span>}
        </div>
      ),
    },
    { key: 'uom', header: t('Unit'), width: 70, render: (l) => l.uomCode || '—' },
    { key: 'theoQty', header: t('Theo qty'), width: 90, align: 'right', render: (l) => qty(l.theoreticalQty) },
    { key: 'actQty', header: t('Actual qty'), width: 95, align: 'right', render: (l) => qty(l.actualQty) },
    { key: 'varQty', header: t('Var qty'), width: 90, align: 'right', render: (l) => <span style={{ color: `var(--color-${varianceTone(l.varianceQty)})` }}>{l.varianceQty > 0 ? '+' : ''}{qty(l.varianceQty)}</span> },
    { key: 'theoCost', header: t('Theo cost'), width: 110, align: 'right', render: (l) => fmtMoney(l.theoreticalCost) },
    { key: 'actCost', header: t('Actual cost'), width: 110, align: 'right', render: (l) => fmtMoney(l.actualCost) },
    {
      key: 'varCost',
      header: t('Variance'),
      width: 150,
      align: 'right',
      render: (l) => (
        <Badge tone={varianceTone(l.varianceCost)}>
          {l.varianceCost > 0 ? '+' : ''}{fmtMoney(l.varianceCost)}{l.variancePercent != null ? ` (${l.variancePercent.toFixed(0)}%)` : ''}
        </Badge>
      ),
    },
  ];

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/usage-variance')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Usage & variance')}
      </Button>

      <PageHeader
        title={editing ? (name || t('Usage analysis')) : t('New usage analysis')}
        description={t('Theoretical usage is exploded from the menu items sold; actual usage comes from the inventory ledger over the period. The variance shows where food cost is leaking.')}
        icon={<Icon.ArrowLeftRight size={22} />}
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {editing && (
              <Button variant="destructive" disabled={submitting} leadingIcon={<Icon.Trash size={15} />} onClick={() => setDeleteOpen(true)}>{t('Delete')}</Button>
            )}
            <Button variant="primary" loading={submitting} leadingIcon={<Icon.Check size={15} />} onClick={save}>
              {editing ? t('Save') : t('Create analysis')}
            </Button>
          </div>
        }
      />

      <div className="uv-layout">
        <div className="uv-main">
          <Card>
            <Card.Body>
              <div className="uv-form">
                <Input label={t('Name')} placeholder={t('e.g. Kitchen — week 23')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                <div className="uv-grid">
                  <Input label={t('Period start')} type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
                  <Input label={t('Period end')} type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
                </div>
                <div className="uv-grid">
                  <div className="ps-field">
                    <span className="ps-field__label">{t('Location scope')}</span>
                    <Select value={scopeLocationId} onChange={setScopeLocationId} options={locationOptions} ariaLabel={t('Location scope')} searchable />
                  </div>
                  <Input label={t('Flag threshold %')} type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} hint={t('Flag items above this variance')} />
                </div>
                <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Context for this study (optional)')} />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>{t('Menu items sold')}</Card.Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                {previewing && <span className="uv-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Spinner size={14} /> {t('Updating…')}</span>}
                <Button variant="ghost" size="sm" leadingIcon={<Icon.Upload size={15} />} onClick={() => setImportOpen(true)}>{t('Import sales')}</Button>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="uv-add">
                <Icon.Plus size={16} />
                <Combobox
                  options={itemOptions}
                  value=""
                  onChange={addItem}
                  placeholder={t('Add a menu item…')}
                  searchPlaceholder={t('Search menu items…')}
                  size="sm"
                  emptyText={menuItems.length === 0 ? t('No menu items — create some in Recipes first') : t('All menu items added')}
                  ariaLabel={t('Add menu item')}
                />
              </div>

              {lines.length === 0 ? (
                <div className="uv-empty">{t('Add the menu items sold during the period (or use Import sales). Theoretical ingredient usage is exploded from each recipe.')}</div>
              ) : (
                <div className="uv-lines">
                  <div className="uv-line uv-line--head">
                    <span>{t('Item')}</span>
                    <span>{t('Units sold')}</span>
                    <span />
                  </div>
                  {lines.map((l) => (
                    <div className="uv-line" key={l.key}>
                      <div className="uv-line__name">{l.name}</div>
                      <Input size="sm" type="number" value={l.unitsSold} placeholder="0" onChange={(e) => updateUnits(l.key, e.target.value)} />
                      <IconButton aria-label={t('Remove')} variant="ghost" size="icon-sm" onClick={() => removeLine(l.key)}><Icon.Trash size={15} /></IconButton>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="uv-side">
          <Card>
            <Card.Header><Card.Title>{t('Food-cost variance')}</Card.Title></Card.Header>
            <Card.Body>
              {!datesValid ? (
                <div className="uv-muted">{t('Set the period dates to compute actual usage from the inventory ledger.')}</div>
              ) : summary ? (
                <div className="uv-summary">
                  <KpiTile label={t('Theoretical cost')} value={fmtMoney(summary.theoreticalCost)} meta={t('{count} menu items', { count: summary.salesItemCount })} />
                  <KpiTile label={t('Actual cost')} value={fmtMoney(summary.actualCost)} meta={t('{count} ledger moves', { count: summary.movementCount })} />
                  <KpiTile
                    label={t('Total variance')}
                    value={(
                      <span style={{ color: `var(--color-${varianceTone(summary.varianceCost)})` }}>
                        {summary.varianceCost > 0 ? '+' : ''}{fmtMoney(summary.varianceCost)}
                      </span>
                    )}
                    meta={summary.variancePercent != null ? t('{percent}% vs theoretical', { percent: summary.variancePercent.toFixed(1) }) : undefined}
                  />
                  <div className="uv-counts">
                    <Badge tone="warning">{t('Flagged {count}', { count: summary.flaggedCount })}</Badge>
                    <Badge tone="danger">{t('Over {count}', { count: summary.unfavorableCount })}</Badge>
                    <Badge tone="success">{t('Under {count}', { count: summary.favorableCount })}</Badge>
                  </div>
                  <div className="uv-hint uv-muted">
                    {t('Positive variance = used more than the recipes predict (over-portioning, waste or shrink). Both sides are valued at current item cost.')}
                  </div>
                </div>
              ) : (
                <div className="uv-muted">{t('Add menu items sold to compute the variance.')}</div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {datesValid && varianceLines.length > 0 && (
        <Card style={{ marginTop: 'var(--space-4)' }}>
          <Card.Header>
            <Card.Title>{t('Item variance')}</Card.Title>
            <Card.Subtitle>{t('Largest unfavorable variance first')}</Card.Subtitle>
          </Card.Header>
          <Card.Body>
            <DataTable
              columns={columns}
              data={varianceLines}
              rowKey={(l) => l.itemId}
              density="compact"
            />
          </Card.Body>
        </Card>
      )}

      <Drawer
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('Import sales')}
        description={t('Paste rows from your POS as “Menu item, units sold” (comma, tab or semicolon separated). Items are matched by name or reference.')}
        size="md"
      >
        <div className="pp-form">
          <Textarea
            label={t('Sales rows')}
            rows={10}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={'Margherita Pizza, 120\nCaesar Salad, 64\nRCP-000007, 22'}
          />
          <div className="pp-form__actions">
            <Button type="button" variant="ghost" onClick={() => setImportOpen(false)}>{t('Cancel')}</Button>
            <Button type="button" variant="primary" leadingIcon={<Icon.Upload size={15} />} onClick={runImport} disabled={!importText.trim()}>{t('Import')}</Button>
          </div>
        </div>
      </Drawer>

      <ConfirmDialog
        open={deleteOpen}
        title={t('Delete analysis')}
        description={t('This permanently removes the usage/variance analysis.')}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={submitting}
        onConfirm={doDelete}
        onCancel={() => (submitting ? null : setDeleteOpen(false))}
      />
    </PageContainer>
  );
}
